import React, { useEffect, useState } from 'react';
import { Container, Button } from '@mui/material';
import { Card, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import StrapiRichText, { strapiRichTextBaseSx } from './StrapiRichText';

const installmentSx = {
  ...strapiRichTextBaseSx,
  textAlign: 'center',
  fontSize: '1rem',
  fontFamily: 'Montserrat',
  fontWeight: 500,
  color: '#2f3036',
  '& p': { margin: 0 },
  '& p:not(:last-child)': { marginBottom: '0.25rem' },
};

function SectionHeading({ before, highlight }) {
  return (
    <h3>
      {before}{' '}
      <span style={{ color: 'black', backgroundColor: '#E1EFFF', fontWeight: '800', letterSpacing: '1.2px' }}>
        {highlight}
      </span>
    </h3>
  );
}

function TestimonialsHeading({ before, highlight }) {
  return (
    <h3>
      {before} <span style={{ color: 'black', fontWeight: '800', letterSpacing: '1.2px' }}>{highlight}</span>
    </h3>
  );
}

function AttributionLine({ text }) {
  const idx = text.indexOf(' - ');
  const baseStyle = {
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: '300',
    marginTop: '2rem',
    letterSpacing: '1.2px',
    textAlign: 'left',
  };
  if (idx === -1) {
    return <Card.Text style={baseStyle}>{text}</Card.Text>;
  }
  return (
    <Card.Text style={baseStyle}>
      {text.slice(0, idx)} - <strong>{text.slice(idx + 3)}</strong>
    </Card.Text>
  );
}

function ForfaitCard({ forfait }) {
  const suffix = forfait.priceSuffix || 'TTC';
  const btn = forfait.buttonLabel || "S'inscire";
  return (
    <Card className="text-center border-1 shadow">
      <Card.Body>
        <Card.Title style={{ fontSize: '3rem', fontFamily: 'Montserrat', fontWeight: '700', marginBottom: '1rem', marginTop: '1rem' }}>
          {forfait.cardTitle}
        </Card.Title>
        {forfait.cardTagline ? (
          <Card.Text style={{ fontSize: '1rem', fontFamily: 'Montserrat', fontWeight: '400', textDecoration: 'underline' }}>
            {forfait.cardTagline}
          </Card.Text>
        ) : null}
        <Card.Text style={{ fontSize: '5rem', fontFamily: 'Montserrat', fontWeight: '700' }}>
          <span style={{ fontSize: '30px' }}>₪</span>
          {forfait.priceAmount}
          <span style={{ fontSize: '10px' }}>{suffix}</span>
        </Card.Text>
        {forfait.installment ? (
          <div style={{ marginBottom: '0.5rem' }}>
            <StrapiRichText source={forfait.installment} sx={installmentSx} />
          </div>
        ) : null}
        <hr style={{ marginBottom: '2rem' }} />
        {forfait.featureLines.map((line, i) => (
          <Card.Text
            key={`${i}-${line.slice(0, 24)}`}
            style={{ textAlign: 'left', fontFamily: 'Montserrat', fontSize: '1rem', marginLeft: '1rem', marginBottom: '1.5rem' }}
          >
            <span style={{ marginRight: '0.5em' }}>✔️</span> {line}
          </Card.Text>
        ))}
        {forfait.contractPath ? (
          <Link to={forfait.contractPath}>
            <Button className="purchase" style={{ color: 'white', backgroundColor: '#2593C4', width: '150px', fontSize: '16px', marginTop: '3rem' }}>
              {btn}
            </Button>
          </Link>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function ServiceCard({ card }) {
  const suffix = card.priceSuffix || 'TTC';
  const btn = card.buttonLabel || "S'inscire";
  return (
    <Card className="text-center border-1 shadow d-flex flex-column h-100" style={{ borderRadius: '20px' }}>
      <Card.Body className="d-flex flex-column h-100">
        <div className="flex-grow-1">
          <Card.Title style={{ fontSize: '1.5rem', fontFamily: 'Montserrat', fontWeight: '700', marginBottom: '2rem', marginTop: '1rem' }}>
            {card.titleLine1}
            {card.titleLine2 ? (
              <>
                <br />
                {card.titleLine2}
              </>
            ) : null}
          </Card.Title>
          <Card.Text style={{ fontSize: '4rem', fontFamily: 'Montserrat', fontWeight: '700', color: '#16163F' }}>
            ₪{card.priceAmount}
            <span style={{ fontSize: '10px' }}>{suffix}</span>
          </Card.Text>
          {card.installment ? (
            <div style={{ marginBottom: '0.25rem' }}>
              <StrapiRichText source={card.installment} sx={installmentSx} />
            </div>
          ) : null}
          {card.featureLines.map((line, i) => (
            <Card.Text
              key={`${i}-${line.slice(0, 24)}`}
              style={{ textAlign: 'left', fontFamily: 'Montserrat', fontSize: '1rem', marginLeft: '1rem', marginBottom: '1.5rem' }}
            >
              <span style={{ marginRight: '0.5em' }}>✔️</span> {line}
            </Card.Text>
          ))}
        </div>
        <div style={{ marginTop: 'auto' }}>
          {card.contractPath ? (
            <Link to={card.contractPath}>
              <Button className="purchase" style={{ color: 'white', backgroundColor: '#2593C4', width: '150px', fontSize: '16px', marginTop: '1rem' }}>
                {btn}
              </Button>
            </Link>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
}

/**
 * Blocs sous le hero (données déjà fusionnées Strapi + défauts).
 */
export default function ExpertConnectionSections({ sections }) {
  const [activeSet, setActiveSet] = useState(1);

  const {
    featureColumns,
    forfaitsSectionTitleBefore,
    forfaitsSectionTitleHighlight,
    forfaits,
    servicesSectionTitleBefore,
    servicesSectionTitleHighlight,
    serviceCards,
    ctaTitle,
    ctaBody,
    testimonialsSectionTitleBefore,
    testimonialsSectionTitleHighlight,
    testimonials,
  } = sections;

  const nForfaits = forfaits.length;

  useEffect(() => {
    setActiveSet((prev) => Math.min(prev, Math.max(nForfaits, 1)));
  }, [nForfaits]);

  const safeActive = Math.min(Math.max(activeSet, 1), Math.max(nForfaits, 1));
  const currentForfait = forfaits[safeActive - 1];

  return (
    <>
      <Container maxWidth="lg" style={{ marginTop: '3rem', marginBottom: '4rem' }}>
        <Row>
          {featureColumns.map((col, idx) => (
            <Col md={4} key={`${col.title}-${idx}`}>
              <Card className="border-0">
                <Card.Body>
                  <Card.Title style={{ color: '#2593C4', fontFamily: 'Montserrat', fontWeight: '700' }}>{col.title}</Card.Title>
                  <Card.Text style={{ fontFamily: 'Montserrat' }}>{col.body}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      <Container maxWidth="md" style={{ marginBottom: '1rem' }}>
        <SectionHeading before={forfaitsSectionTitleBefore} highlight={forfaitsSectionTitleHighlight} />
      </Container>

      <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
        {nForfaits > 1 ? (
          <Row>
            <Col md={12} xs={12} className="d-flex flex-row flex-wrap align-items-center justify-content-center" style={{ marginBottom: '3rem' }}>
              {forfaits.map((f, idx) => {
                const n = idx + 1;
                const label = f.tabLabel?.trim() || `Forfait ${n}`;
                return (
                  <Button
                    key={`tab-${n}`}
                    variant="outline-secondary"
                    style={{
                      borderRadius: '20px',
                      border: '1px solid #6c757d',
                      margin: '5px',
                      color: safeActive === n ? 'black' : '#e0e0e0',
                      backgroundColor: safeActive === n ? '#f8f9fa' : 'transparent',
                    }}
                    onClick={() => setActiveSet(n)}
                  >
                    {label}
                  </Button>
                );
              })}
            </Col>
          </Row>
        ) : null}
        <Col md={{ span: 6, offset: 3 }} xs={12}>
          {currentForfait ? <ForfaitCard forfait={currentForfait} /> : null}
        </Col>
      </Container>

      <div style={{ backgroundColor: '#FAFAFA', width: '100%', padding: 0 }}>
        <Container maxWidth="md" style={{ marginTop: '1.5rem', marginBottom: '3rem' }}>
          <SectionHeading before={servicesSectionTitleBefore} highlight={servicesSectionTitleHighlight} />
        </Container>
        <Container maxWidth="xl" style={{ marginTop: '3rem', marginBottom: '1rem' }}>
          <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
            {serviceCards.map((card, idx) => (
              <Col md={3} className="mb-4" key={`${card.titleLine1}-${idx}`}>
                <ServiceCard card={card} />
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      <div className="outer-container">
        <div className="you-want">
          <Container className="d-flex justify-content-center align-items-center">
            <Col md={6} className="mx-auto">
              <Card className="special-card shadow" style={{ marginTop: '4rem' }}>
                <Card.Body style={{ padding: '30px 16px 30px 16px' }}>
                  <Card.Text style={{ fontSize: '3rem', fontFamily: 'Montserrat', fontWeight: '700', textAlign: 'center', color: '#16163F', marginTop: '3rem' }}>
                    {ctaTitle}
                  </Card.Text>
                  <Card.Text style={{ fontSize: '1rem', fontFamily: 'Montserrat', textAlign: 'center', letterSpacing: '1.2px', marginTop: '2rem', marginBottom: '2rem', color: 'black', padding: '0px 2rem 0px 2rem' }}>
                    {ctaBody}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Container>
        </div>
        <div style={{ minHeight: '300px', marginTop: '-10%' }} />
      </div>

      <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
        <TestimonialsHeading before={testimonialsSectionTitleBefore} highlight={testimonialsSectionTitleHighlight} />
      </Container>
      <Container maxWidth="xl" style={{ marginBottom: '3rem' }}>
        <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {testimonials.map((t, idx) => (
            <Col md={4} className="mb-4" key={`${t.attribution}-${idx}`}>
              <Card className="text-center border-1 shadow d-flex flex-column h-100" style={{ borderRadius: '0px' }}>
                <Card.Body className="d-flex flex-fill flex-column">
                  <Card.Text style={{ fontFamily: 'Roboto', fontSize: '16px', fontStyle: 'italic', fontWeight: '300', marginTop: '2rem', letterSpacing: '1.2px' }}>
                    &ldquo;{t.quote}&rdquo;
                  </Card.Text>
                  <hr style={{ width: '70px', marginTop: '3rem' }} />
                  <AttributionLine text={t.attribution} />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </>
  );
}
